import { useEffect, useMemo, useState } from 'react'
import { Money } from '../../platform/primitives/Money'
import { shortDate } from './backfillWindow'
import { isSavable, toWrite, type RuleDraft } from './ruleWire'
import { useUnsavedPreview, type PreviewBody } from './useRules'

const DEBOUNCE_MS = 400
const SAMPLE_LIMIT = 5

/**
 * The live preview. An ESTIMATE, on purpose: until backend issue 444 lands, an
 * unsaved rule is scanned against only the newest 1,000 rows, so the count is
 * qualified "About". The exact count is shown at confirmation. Advisory only —
 * an error never blocks Save.
 */
export function RulePreview({ draft }: { draft: RuleDraft }) {
  const savable = isSavable(draft)
  const serialised = useMemo(
    () =>
      savable
        ? JSON.stringify({
            conditions: toWrite(draft).conditions,
            actions: toWrite(draft).actions,
            limit: SAMPLE_LIMIT,
          })
        : null,
    [savable, draft],
  )

  // The body settles after 400ms without an edit; `null` parks the query.
  const [settled, setSettled] = useState<string | null>(null)
  useEffect(() => {
    if (serialised === null) {
      setSettled(null)
      return
    }
    const t = setTimeout(() => setSettled(serialised), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [serialised])

  const body = useMemo(() => (settled === null ? null : (JSON.parse(settled) as PreviewBody)), [settled])
  const { data, isFetching, isError, isPlaceholderData } = useUnsavedPreview(body)

  // `keepPreviousData` holds the last result across a parked (null) body, so
  // `data` may answer an OLDER draft. A count is shown — and announced — only
  // when the settled body is the current one and nothing is in flight.
  const pending = savable && (settled !== serialised || isFetching)
  const shown = savable && !pending ? data : undefined

  const countText = (n: number) =>
    n === 0
      ? 'No recent transactions match'
      : `About ${n} recent ${n === 1 ? 'transaction matches' : 'transactions match'}`

  // What the live region says. ONLY a settled result is ever in it: "Checking…"
  // is visual (below), so an edit burst announces once, at the count. A refetch
  // of the SAME body (window refocus) keeps the count already there — the text
  // does not change, so nothing is announced. While a NEW draft is unanswered
  // the region is empty; `isPlaceholderData` stops it holding the old draft's.
  let announced = 'Add a condition to preview matches'
  if (savable) {
    if (isError && !pending) announced = 'Preview unavailable right now.'
    else if (shown !== undefined) announced = countText(shown.matchedCount)
    else if (settled === serialised && data !== undefined && !isPlaceholderData && !isError)
      announced = countText(data.matchedCount)
    else announced = ''
  }
  const checking = savable && pending
  const matched = shown !== undefined && !isError && shown.matchedCount > 0

  return (
    <section
      className="rule-preview"
      aria-label="Preview"
      aria-busy={pending}
      data-testid="rule-preview"
    >
      <div className="rule-preview__head">
        {/* Decoration: the title beside it says the same thing in words. */}
        <span
          className={matched ? 'rule-preview__dot rule-preview__dot--on' : 'rule-preview__dot'}
          aria-hidden="true"
        />
        {/* Polite live region: the SETTLED result only (#511). While a check
            is running the text stays put but is visually hidden, and the
            "Checking…" line below stands in for it — outside the region, so a
            screen reader never hears it. */}
        <p
          className={checking ? 'rule-preview__title sr-only' : 'rule-preview__title'}
          role="status"
          aria-live="polite"
        >
          {announced}
        </p>
        {checking && (
          <p className="rule-preview__title" aria-hidden="true">
            Checking recent transactions…
          </p>
        )}
        <span className="rule-preview__label" aria-hidden="true">
          Preview
        </span>
      </div>
      {!savable && <p className="rule-preview__note">Fill in at least one condition and one action.</p>}
      {shown !== undefined && !isError && (
        <>
          {shown.matchedCount === 0 ? (
            <p className="rule-preview__note">
              That’s fine — the rule will still catch new transactions as they sync.
            </p>
          ) : (
            <ul className="rule-preview__samples">
              {shown.samples.slice(0, SAMPLE_LIMIT).map((s) => (
                <li key={s.transactionId} className="rule-preview__sample">
                  <span className="rule-preview__date">{shortDate(s.date)}</span>
                  <span className="rule-preview__merchant">{s.merchantName ?? 'Unknown merchant'}</span>
                  <span className="rule-preview__amount">
                    <Money cents={s.amountCents} kind="flow" />
                  </span>
                  <span
                    className={
                      s.changes.length > 0
                        ? 'rule-preview__status rule-preview__status--change'
                        : 'rule-preview__status'
                    }
                  >
                    {s.changes.length > 0 ? 'Would change' : 'No change'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="rule-preview__note">
            Estimated from your most recent 1,000 transactions. The exact count is shown before
            anything changes.
          </p>
        </>
      )}
    </section>
  )
}
